// crewbit-admin/src/components/money/BusinessPickerPopover.tsx
//
// Popover picker for the admin sidebar BusinessSwitcherHeader.
// Spec: docs/references/MONEY_MODULE_WIRING_v1.md §7.2.
//
// Renders below the trigger as an absolutely-positioned card. Outside-click
// and Escape are handled by the parent (BusinessSwitcherHeader) since the
// trigger holds the ref needed for hit-testing.

import { Check, Building2 } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';
import type { Membership } from '../../hooks/useActiveBusiness';

interface Props {
  memberships: Membership[];
  activeBusinessId: string | null;
  onSelect: (businessId: string) => void | Promise<void>;
  onClose: () => void;
}

export default function BusinessPickerPopover({
  memberships,
  activeBusinessId,
  onSelect,
}: Props) {
  return (
    <div
      role="menu"
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        right: 0,
        zIndex: 50,
        background: palette.white,
        borderRadius: 10,
        border: `1px solid ${palette.g150}`,
        boxShadow: '0 8px 28px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.08)',
        padding: 6,
        maxHeight: 320,
        overflowY: 'auto',
        animation: 'businessPickerIn 120ms ease-out',
      }}
    >
      <style>{`
        @keyframes businessPickerIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: palette.g500,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          padding: '8px 10px 4px',
        }}
      >
        Switch business
      </div>
      {memberships.map((m) => {
        const isActive = m.id === activeBusinessId;
        return (
          <button
            key={m.id}
            type="button"
            role="menuitem"
            onClick={() => {
              void onSelect(m.id);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '8px 10px',
              border: 'none',
              borderRadius: 8,
              background: isActive ? palette.accentWash : 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              textAlign: 'left',
              transition: `background ${transition.fast}`,
              color: palette.g800,
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.background = palette.bg;
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.background = 'transparent';
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: isActive ? palette.accentSoft : palette.g100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isActive ? palette.accent : palette.g500,
                flexShrink: 0,
              }}
            >
              <Building2 size={14} strokeWidth={1.75} />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? palette.accent : palette.g800,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {m.name}
            </div>
            {isActive && <Check size={14} color={palette.accent} strokeWidth={2.5} />}
          </button>
        );
      })}
    </div>
  );
}
